import Vue from "vue";
import api from "../../api/auth";
import { EventBus } from "@/eventBus";
import { EMPLOYEE_STATE_INACTIVE, EMPLOYEE_STATE_READY, EMPLOYEE_STATE_TRAINING } from "@/constants/employee";
import apiContour from "@/services/apiContour";
import googleAutocompleteAudit from "@/services/googleAutocompleteAudit";

const KEEP_ALIVE_INTERVAL_MS = 200000;

function applyAuthenticatedApiContour(contour) {
  if (contour) {
    apiContour.activate(contour);
  }
}

function syncKeepAliveHeaders(dispatch) {
  return api.keepAlive()
    .then(({ headers }) => {
      dispatch("syncAuthHeaders", headers);
      return headers;
    });
}

export default {
  state: {
    user: null,
    time: null,
    changeReady: null,
    timezones: null,
  },
  getters: {
    getAuthUser: (state) => state.user,
    getAuthUserToken: (state) => state.user && state.user.api_token,
    getTimezones(state) {
      const { timezones } = state;
      if (!timezones) return [];

      return Object.entries(timezones).map(([id, text]) => {
        return {
          id: +id,
          text,
        };
      });
    },
  },
  mutations: {
    SET_AUTH_DATA(state, payload) {
      state.user = payload;
    },
    SET_TIME_DATA(state, payload) {
      state.time = payload;
    },
    SET_CHANGE_READY_DATA(state, payload) {
      state.changeReady = payload;
    },
    SET_TIMEZONES_DATA(state, payload) {
      state.timezones = payload;
    }
  },
  actions: {
    syncAuthHeaders(ctx, headers = {}) {
      const nextToken = headers["x-auth-token"];
      const nextExpiration = headers["x-api-token-expires"];
      const previousToken = window.localStorage.getItem("token");

      if (nextToken) {
        Vue.auth.setToken(nextToken, nextExpiration);
        if (previousToken && previousToken !== nextToken) {
          EventBus.$emit("auth-token-updated", nextToken);
        }
        return;
      }

      Vue.auth.updateTokenExpiration(nextExpiration);
    },
    loginUser({ commit, dispatch }, payload) {
      const { data, vm } = payload;
      dispatch("setIsLoadingData", true);
        return api.login(data).then(({data: {data}, headers}) => {
        const { api_token, api_token_expired, api_contour, username } = data;
        const userId = headers['x-user-id'];

        vm.$auth.setUser(username, "user", userId);
        vm.$auth.setToken(api_token, api_token_expired);
        applyAuthenticatedApiContour(api_contour);

        dispatch("fetchEmployee");

        commit("SET_AUTH_DATA", data);

        return new Promise((resolve, reject) => {
          if (api_token !== "") {
            return resolve(data);
          } else {
            return reject(false);
          }
        });
      });
    },
    logoutUser({ dispatch, rootGetters }, payload) {
      const { vm } = payload;

      let state = +window.sessionStorage.getItem('state');
      if (rootGetters.isEmployeeOperator && state !== EMPLOYEE_STATE_TRAINING) {
        dispatch("changeReady", {
          state: EMPLOYEE_STATE_INACTIVE,
          target: "logout",
        });
      }

      api.logout().then(() => {
        dispatch("destroySession", vm);
        window.location.reload();
      });
    },
    autoLoginByToken({ commit, dispatch }, payload) {
      const { vm } = payload;

      dispatch("destroySession", vm);
      dispatch("setIsLoadingData", true);

      const urlParams = new URLSearchParams(window.location.search);
      const userData = {
        username: urlParams.get("username"),
        api_token: urlParams.get("api_token"),
        api_token_expired: urlParams.get("api_token_expired"),
      };

        return new Promise((resolve, reject) => {
        return api.token(userData).then(({ data: { status, data = {} }, headers }) => {
          const userId = headers['x-user-id'];

          if (status === "success") {
            const authenticatedUserData = {
              ...userData,
              api_contour: data.api_contour,
            };
            vm.$auth.setUser(userData.username, "user", userId);
            vm.$auth.setToken(userData.api_token, userData.api_token_expired);
            applyAuthenticatedApiContour(data.api_contour);
            commit("SET_AUTH_DATA", authenticatedUserData);

            return resolve(authenticatedUserData);
          } else {
            return reject(false);
          }
        });
      });
    },
    registerUser({ dispatch }, payload) {
      const { data } = payload;
      dispatch("setIsLoadingData", true);

            return api.register(data).then(({
                data: {
                    data
                }
            }) => {
                return new Promise((resolve, reject) => {
                    if (data) {
                        return resolve(data);
                    } else {
                        return reject(data);
                    }
                });
            });
        },
    destroySession({ dispatch }, payload) {
            googleAutocompleteAudit.stop();
            dispatch("toggleKeepAlive", EMPLOYEE_STATE_INACTIVE);
            payload.$auth.destroyToken();
            payload.$onlineToken.destroy();
            window.localStorage.clear();
            window.sessionStorage.clear();
        },
        forgotPass({
            dispatch
        }, payload) {
            const {
                data,
            } = payload;
            dispatch("setIsLoadingData", true);

      return api.forgot(data);
    },
    contactUs({ dispatch }, payload) {
      const { data } = payload;
      dispatch("setIsLoadingData", true);

      return api.sendLetter(data);
    },
    getTime({ commit, dispatch }) {
      return api.getTime().then(
        ({
          data: {
            data: { time },
          },
          headers,
          status,
        }) => {
          if (status === 200) {
            commit("SET_TIME_DATA", time);
            window.sessionStorage.setItem('state', time['state']);
            if (time['state'] === EMPLOYEE_STATE_TRAINING) {
              if (typeof time['trainingId'] !== undefined) {
                window.sessionStorage.setItem('trainingId', time['trainingId']);
              }
            } else {
              window.sessionStorage.removeItem('trainingId');
            }

            dispatch("syncAuthHeaders", headers);
            dispatch("toggleKeepAlive", time['state']);
          }

          return time;
        }
      );
    },
    toggleKeepAlive({ dispatch }, targetState = null) {
      const state = targetState === null
        ? +window.sessionStorage.getItem("state")
        : +targetState;
      const shouldRunKeepAlive = [
        EMPLOYEE_STATE_READY,
        EMPLOYEE_STATE_TRAINING,
      ].includes(state);

      if (shouldRunKeepAlive) {
        const shouldSyncImmediately = !window.keepAliveInterval;

        if (!window.keepAliveInterval) {
          window.keepAliveInterval = setInterval(() => {
            syncKeepAliveHeaders(dispatch).catch(() => {});
          }, KEEP_ALIVE_INTERVAL_MS);
        }

        if (!window.keepAliveOnlineHandler) {
          window.keepAliveOnlineHandler = () => {
            const currentState = +window.sessionStorage.getItem("state");
            const isReadyOrTraining = [
              EMPLOYEE_STATE_READY,
              EMPLOYEE_STATE_TRAINING,
            ].includes(currentState);

            if (isReadyOrTraining) {
              syncKeepAliveHeaders(dispatch).catch(() => {});
            }
          };
          window.addEventListener("online", window.keepAliveOnlineHandler);
        }

        if (shouldSyncImmediately) {
          syncKeepAliveHeaders(dispatch).catch(() => {});
        }
        return;
      }

      if (window.keepAliveInterval) {
        clearInterval(window.keepAliveInterval);
        window.keepAliveInterval = null;
      }

      if (window.keepAliveOnlineHandler) {
        window.removeEventListener("online", window.keepAliveOnlineHandler);
        window.keepAliveOnlineHandler = null;
      }
    },
    changeReady({ commit, dispatch }, payload) {
      dispatch("toggleKeepAlive", payload.state);
      return api
        .changeReady(payload)
        .then(({ data, headers }) => {
          const { time, trainingId } = data.data;
          const nextState = time && typeof time.state !== "undefined"
            ? time.state
            : payload.state;
          if (time) commit("SET_TIME_DATA", time);
          if (time) {
            window.sessionStorage.setItem('state', time.state);
          }
          if (trainingId) {
            window.sessionStorage.setItem('trainingId', trainingId);
          } else if (time?.state !== EMPLOYEE_STATE_TRAINING) {
            window.sessionStorage.removeItem('trainingId');
          }
          dispatch("syncAuthHeaders", headers);
          commit("SET_CHANGE_READY_DATA", data);
          EventBus.$emit("change-ready", +nextState);
          return data;
        })
        .catch((e) => {
          if(e?.response?.data?.data) {
            const { time } = e.response.data.data;
            if (time) commit("SET_TIME_DATA", time);
          }
          commit("SET_CHANGE_READY_DATA", false);
          setTimeout(() => {
            dispatch("toggleKeepAlive");
          }, 0);
          throw e;
        });
    },
    setTimezone({ commit }, payload) {
      const data = {
        timezone_id: payload,
      };
      api
        .setTimezone(data)
        .then(({ status }) => {
          if (status === 200) {
            commit("SET_SNACKBAR_DATA", {
              type: "success",
              text: "Success",
            });
            EventBus.$emit("fetch-employee", true);
          }
        })
        .catch((error) => {
          const {
            response: {
              data: {
                data: { messages },
              },
              status,
            },
          } = error;

          if (status === 400) {
            commit("SET_SNACKBAR_DATA", {
              type: "error",
              text: messages ? messages[0] : "Error",
            });
          }

                    return false;
                });
        },
      setUserEmail({ commit, dispatch }, payload) {
        const { data, vm } = payload;
        dispatch("setIsLoadingData", true);

        return api.setEmail(data).then(({ data: { data }, headers }) => {
          const { api_token, api_token_expired, username } = data;
          const userId = headers['x-user-id'];

          vm.$auth.setUser(username, "user", userId);
          vm.$auth.setToken(api_token, api_token_expired);

          dispatch("fetchEmployee");

          commit("SET_AUTH_DATA", data);

          return new Promise((resolve, reject) => {
            if (api_token !== "") {
              return resolve(data);
            } else {
              return reject(false);
            }
          });
        });
      },
    },
};
